/**
 * Offer File Search API
 * 
 * Wrapper for searching offer files using Elasticsearch
 */
import { v1Api } from '../../../api/generatedClient'
import type { APIOfferFileSearchResponse } from '../../../generated-types/models/APIOfferFileSearchResponse'
import type { APIOfferFileSearchResult } from '../../../generated-types/models/APIOfferFileSearchResult'
import type { V1SearchRequestsOfferFilesRetrieveRequest } from '../../../generated-types/apis/V1Api'
import { createFileInfo } from '../../../utils/functions'
import type { FileInfo } from '../../../utils/types'

export interface OfferFileSearchFilters {
  q: string
  offerId?: number
  requestId?: number
  requestCertificate?: string
  fileName?: string
  storedAt?: string // YYYY-MM-DD
  fileSize?: string // File size (e.g., '10287', '10.05', '10.05 KB', '1.5 MB')
  fields?: string[]
  size?: number
  from?: number
}

export interface OfferFileSearchResponse {
  total: number
  max_score: number | null
  results: FileInfo[]
  page: {
    from: number
    size: number
    has_more: boolean
  }
}

function toOfferFileSearchRequest(
  filters: OfferFileSearchFilters
): V1SearchRequestsOfferFilesRetrieveRequest {
  if (!filters.q || filters.q.trim() === '') {
    throw new Error('Search query (q) is required')
  }

  return {
    q: filters.q,
    fields: filters.fields && filters.fields.length > 0 ? filters.fields.join(',') : undefined,
    size: filters.size,
    from: filters.from,
    offerId: filters.offerId,
    requestId: filters.requestId,
    requestCertificate: filters.requestCertificate,
    fileName: filters.fileName,
    storedAt: filters.storedAt ? new Date(filters.storedAt) : undefined,
    fileSize: filters.fileSize,
  }
}

/**
 * Search for offer files using Elasticsearch
 * 
 * @param filters - Search filters
 * @returns Offer file search response with results and pagination info
 */
export async function searchOfferFiles(
  filters: OfferFileSearchFilters
): Promise<OfferFileSearchResponse> {
  const response: APIOfferFileSearchResponse = await v1Api.v1SearchRequestsOfferFilesRetrieve(
    toOfferFileSearchRequest(filters)
  )

  // Map results to FileInfo
  const fileInfos = response.results.map((result) => {
    const fileInfo: Record<string, unknown> = {
      self: `/v1/request/offer/files/${result.id}/`,
      name: result.fileName ?? '',
      key: result.key ?? '',
      size: result.fileSize ?? 0,
      stored_at: result.storedAt?.toISOString(),
      last_modified_at: result.lastModifiedAt?.toISOString(),
    }

    if (result.offerId) {
      fileInfo.offer = `/v1/request/offers/${result.offerId}/`
    }

    return createFileInfo(fileInfo)
  })

  return {
    total: response.total,
    max_score: response.maxScore,
    results: fileInfos,
    page: {
      from: response.page.fromValue,
      size: response.page.size,
      has_more: response.page.hasMore,
    },
  }
}
